import type { ChangeEvent, FormEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Button, InputField, Icon, IconButton, Link, Typography } from '@branchjs/neo-components';
import LoggedoutActions from '../../../main/actions/LoggedoutActions';
import LoggedoutStore from '../../../main/stores/LoggedoutStore';
import { ImageService } from '../../../main/services/ImageService/ImageService';
import { emailValid as _emailValid, inputTooLong } from '../ScreenSignupValidation';
import type { ErrorFootnotes } from './ScreenLogin.types';
import { useStyles } from './ScreenLogin.styles';
import LoggedOutContainer from '../components/LoggedOutContainer/LoggedOutContainer';

const GITHUB_CLIENT_ID = (window as any).GITHUB_CLIENT_ID as string;

const ScreenLogin = () => {
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [errors, setErrors] = useState<any>(LoggedoutStore.getErrors());
  const [loadingSignIn, setLoadingSignIn] = useState<boolean>(false);
  const [gitHubLoadingSignIn, setGitHubLoadingSignIn] = useState<boolean>(false);
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [passwordValid, setPasswordValid] = useState<boolean>(false);
  const [errorFootnotes, setErrorFootnotes] = useState<ErrorFootnotes>({ email: null, password: null });
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();

  const _preventDefault = (event: Event | FormEvent) => event.preventDefault();

  const _validateEmail = (input: string) => {
    const valid = _emailValid(input);
    setEmailValid(valid);
    setErrorFootnotes({
      ...errorFootnotes,
      email: !valid
        ? 'Please specify a valid email address e.g., myname@email.com.'
        : inputTooLong(input)
        ? 'Please specify a shorter email.'
        : '',
    });
  };

  const _handleEmailChange = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = ev.target.value;
    setEmail(input);
    _validateEmail(input);
    localStorage.setItem('reset_password_user_email', input);
    LoggedoutActions.clearErrors();
  };

  const _validatePassword = (input: string) => {
    setErrorFootnotes({ ...errorFootnotes, password: !input ? 'Please specify a password.' : '' });
    setPasswordValid(!!input);
  };

  const _handlePasswordChange = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = ev.target.value;
    setPassword(input);
    _validatePassword(input);
    LoggedoutActions.clearErrors();
  };

  const _login = () => {
    let currentEmail = email;
    let currentPassword = password;

    // Handle Ugly Safari Autofill -- https://github.com/facebook/react/issues/1159
    if (!currentEmail && emailInputRef.current) {
      currentEmail = emailInputRef.current.value;
      setEmail(currentEmail);
    }
    if (!currentPassword && passwordInputRef.current) {
      currentPassword = passwordInputRef.current.value;
      setPassword(currentPassword);
    }

    if (!currentEmail || !currentPassword) {
      const currentErrorFootnotes = { ...errorFootnotes };
      currentErrorFootnotes.email = !currentEmail ? 'Please specify a valid email address e.g., myname@email.com.' : null;
      currentErrorFootnotes.password = !currentPassword ? 'Please specify a password.' : null;
      setErrorFootnotes(currentErrorFootnotes);
      setLoadingSignIn(false);
    } else {
      setLoadingSignIn(true);
      const queryParams = new URLSearchParams(location.search);
      const nextPath = queryParams.get('next_path') || false;
      // @ts-ignore
      LoggedoutActions.login(currentEmail, currentPassword, nextPath);
    }
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      _login();
    }
  };

  const handleChange = () => {
    const externalErrors = LoggedoutStore.getErrors();
    setErrors(externalErrors);
    if (externalErrors.login_attempts) {
      return history.push('/reset-password');
    }
    if (externalErrors && Object.keys(externalErrors).length !== 0) {
      setLoadingSignIn(false);
    }
    if (externalErrors.email_verification) {
      localStorage.setItem('accept_invite_user_email', email ?? '');
      history.push('/accept-email-invite');
    }
  };

  useEffect(() => {
    /* componentWillMount */
    window.addEventListener('keypress', handleKeyPress);
    LoggedoutStore.addChangeListener(handleChange);

    /* componentDidMount */
    LoggedoutActions.clearErrors();
    if (formRef.current) {
      formRef.current.addEventListener('invalid', _preventDefault, true);
    }

    return () => {
      window.removeEventListener('keypress', handleKeyPress);
      LoggedoutStore.removeChangeListener(handleChange);
      if (formRef.current) {
        formRef.current.removeEventListener('invalid', _preventDefault, true);
      }
    };
  }, [handleKeyPress, handleChange]);

  const _gitHubLoadingSignIn = () => {
    setGitHubLoadingSignIn(true);
  };

  const _renderErrors = (key: string | number) => {
    return errorFootnotes[key] ? errorFootnotes[key] : errors[key];
  };

  return (
    <LoggedOutContainer title="Sign in" linkTitle="New user?" linkText="Create an account" linkPathName="/">
      <form role="form" id="form_login" ref={formRef} onSubmit={_preventDefault}>
        <div className={classes.workEmail}>
          <InputField
            id="email"
            type="text"
            title="Work email"
            name="email"
            onChange={_handleEmailChange}
            value={email ?? ''}
            inputRef={emailInputRef}
            placeholder="Enter your work email"
            error={_renderErrors('email') || _renderErrors('account_blocked')}
            testid="work-email-input-input"
            success={emailValid}
            fullWidth
          />
        </div>
        <div className={classes.password}>
          <InputField
            id="password-input"
            type={passwordVisible ? 'text' : 'password'}
            title="Password"
            name="password"
            onChange={_handlePasswordChange}
            value={password ?? ''}
            inputRef={passwordInputRef}
            testid="password-input-input"
            placeholder="Enter your password"
            error={_renderErrors('password')}
            success={passwordValid}
            // displayShowHide={true}
            // pasteDisabled={true}
            autoComplete="current-password"
            fullWidth
            customTitleEndAdornment={
              <IconButton
                id="showHidePasswordButton"
                size="small"
                color={passwordVisible ? 'primary' : 'secondary'}
                edge="end"
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                <Icon id="showHidePasswordIcon">{passwordVisible ? 'visibility' : 'visibility_off'}</Icon>
                <Typography variant="body2" color="primary" className={classes.showHidePasswordLink}>
                  {passwordVisible ? 'Hide' : 'Show'}
                </Typography>
              </IconButton>
            }
          />
        </div>
        <div className={classes.forgotPassword}>
          <Link id="forgot-password" component={ReactRouterLink} to={{ pathname: '/reset-password', search: '' }}>
            Forgot password?
          </Link>
        </div>
        <div className={classes.signIn}>
          <Button
            variant="contained"
            onClick={_login}
            testid="btn-sign-in"
            loading={loadingSignIn}
            className={classes.buttonSignIn}
          >
            Sign in
          </Button>
        </div>
      </form>
      <div className={classes.footer}>
        <div>
          <img src={ImageService.getImageUrl('/static/images/loggedout/line.svg')} />
          Sign in with
          <img src={ImageService.getImageUrl('/static/images/loggedout/line.svg')} />
        </div>
        <div>
          <Button
            href={`https://github.com/login/oauth/authorize?scope=user:email&client_id=${GITHUB_CLIENT_ID}&state=${
              (window as any)._oauthState
            }`}
            onClick={_gitHubLoadingSignIn}
            loading={gitHubLoadingSignIn}
            testid="sign-in__github"
            className={classes.gitHubButton}
          >
            <i className="fa fa-github fa-fx github-icon" />
            GitHub
          </Button>
        </div>
        <div>
          <span>Questions? We are here to </span>
          <Link href="https://support.branch.io" id="help-support-link">
            help
          </Link>
          <span>.</span>
        </div>
      </div>
    </LoggedOutContainer>
  );
};

export default ScreenLogin;
